(function () {
  // Every video on the page. Rules:
  //  - NOTHING autoplays. Each video idles on its poster until the user starts it.
  //  - at most ONE video plays at a time, and only with sound;
  //  - when a card leaves the screen (slider, page scroll) or the tab is hidden,
  //    its video STOPS. It does NOT resume on its own — the user restarts it.
  //  - to avoid a cold-start delay on click, a video is "warmed" (starts
  //    buffering) on intent — hover / scroll-into-view / pointer-down — while the
  //    markup keeps preload="none" so the initial page load stays light.
  const allVideos = [];

  const whenIdle = window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : function (cb) { return setTimeout(cb, 200); };

  function toggleFor(video) {
    return video
      .closest('.testimonial-video-card')
      ?.querySelector('.video-play-toggle');
  }

  // The desktop/mobile pair share a card; only one is actually rendered.
  function visibleOf(pair) {
    return pair.find((v) => v.offsetParent !== null) || pair[0];
  }

  // Start buffering ahead of the click so playback begins promptly. Runs once,
  // and never on a video that's already playing (load() would interrupt it).
  function warm(video) {
    if (!video || video.dataset.warmed === 'true') return;
    if (video.dataset.userActivated === 'true' || !video.paused) return;
    video.dataset.warmed = 'true';
    try {
      video.preload = 'auto';
      video.load();
    } catch (e) {}
  }

  // Stop the video and reset it to its inactive state (play button shown).
  function idle(video) {
    video.dataset.userActivated = 'false';
    video.muted = true;
    video.loop = true;
    const btn = toggleFor(video);
    btn?.classList.remove('is-playing');
    btn?.setAttribute('aria-label', 'Play video');
    if (!video.paused) video.pause();
  }

  // Unmute and play THIS video from the start. Any other playing video stops.
  function activate(video) {
    allVideos.forEach((v) => {
      if (v === video || !v.isConnected) return;
      if (v.dataset.userActivated === 'true') idle(v);
    });

    // It's loaded now — no need to warm it again later.
    video.dataset.warmed = 'true';
    video.dataset.userActivated = 'true';
    video.muted = false;
    video.loop = false;
    // Play from the top, not from wherever it happened to be.
    try {
      video.currentTime = 0;
    } catch (e) {}
    video.play().catch(() => {});

    const btn = toggleFor(video);
    btn?.classList.add('is-playing');
    btn?.setAttribute('aria-label', 'Pause video');
  }

  function initVideos(section) {
    const cardData = []; // { card, pair }
    const videoCards = section.querySelectorAll('.testimonial-video-card');

    videoCards.forEach((card) => {
      const desktopVideo = card.querySelector('.testimonial-video-desktop video');
      const mobileVideo = card.querySelector('.testimonial-video-mobile video');
      const toggleBtn = card.querySelector('.video-play-toggle');

      const pair = [desktopVideo, mobileVideo].filter(Boolean);

      if (!toggleBtn || !pair.length) return;

      // Whichever of the desktop/mobile pair is actually rendered right now.
      const activeVideo = () => visibleOf(pair);

      pair.forEach((video) => {
        allVideos.push(video);

        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.dataset.userActivated = 'false';
        // No autoplay: the video idles on its poster until the user starts it.

        // The active video was paused (in view) by the user -> go idle.
        video.addEventListener('pause', () => {
          if (video.dataset.userActivated !== 'true') return;
          video.dataset.userActivated = 'false';
          video.muted = true;
          video.loop = true;
          const btn = toggleFor(video);
          btn?.classList.remove('is-playing');
          btn?.setAttribute('aria-label', 'Play video');
        });

        // A finished (non-looping) video -> idle.
        video.addEventListener('ended', () => idle(video));

        // Tap-to-toggle on touch devices (no hover gesture).
        video.addEventListener('click', () => {
          if (!window.matchMedia('(hover: none)').matches) return;

          if (video.dataset.userActivated === 'true') {
            video.pause(); // -> pause handler returns it to idle
            return;
          }
          activate(video);
        });
      });

      // Play/pause button: start this card from the top, or (if already playing)
      // stop it.
      toggleBtn.addEventListener('click', () => {
        const video = activeVideo();
        if (!video) return;

        if (video.dataset.userActivated === 'true') {
          video.pause(); // -> pause handler returns it to idle
          return;
        }
        activate(video);
      });

      // Warm on intent so the click doesn't wait on a cold fetch.
      card.addEventListener('pointerdown', () => warm(activeVideo()));
      card.addEventListener('mouseenter', () => {
        card.classList.add('show-controls'); // hover reveal — unchanged
        warm(activeVideo());
      });
      card.addEventListener('mouseleave', () => card.classList.remove('show-controls'));

      cardData.push({ card, pair });
    });

    return cardData;
  }

  function init(section) {
    const scroller = section.querySelector('.testimonial-scroll');
    const cards = scroller?.querySelectorAll('.testimonial-card-main');
    const prev = section.querySelector('.carousel-btn.prev');
    const next = section.querySelector('.carousel-btn.next');
    const dotsWrap = section.querySelector('.carousel-dots');

    if (!scroller || !cards.length) return;

    const cardData = initVideos(section);

    dotsWrap.innerHTML = '';
    cards.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.addEventListener('click', () => {
        scroller.scrollTo({ left: cards[i].offsetLeft, behavior: 'smooth' });
      });
      dotsWrap.appendChild(dot);
    });

    function update() {
      const max = scroller.scrollWidth - scroller.clientWidth;
      const x = scroller.scrollLeft;
      let active = 0;

      cards.forEach((c, i) => {
        if (x >= c.offsetLeft - c.offsetWidth / 2) active = i;
      });

      dotsWrap.querySelectorAll('button').forEach((d, i) =>
        d.classList.toggle('active', i === active)
      );

      prev.classList.toggle('is-disabled', x <= 2);
      next.classList.toggle('is-disabled', x >= max - 2);
    }

    // Navigation.
    prev.addEventListener('click', () => {
      scroller.scrollBy({ left: -cards[0].offsetWidth, behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      scroller.scrollBy({ left: cards[0].offsetWidth, behavior: 'smooth' });
    });

    scroller.addEventListener(
      'scroll',
      () => {
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    requestAnimationFrame(update);
    requestAnimationFrame(() => requestAnimationFrame(update));
    window.addEventListener('load', update, { once: true });

    // --- Visibility ---
    // Stop a PLAYING video when its card leaves the screen (the scroller clips
    // its overflow, so a sideways slide and a vertical page-scroll both report
    // as "not intersecting"). When a card comes into view, warm its video (on
    // idle time) so the eventual click plays without a cold-start wait.
    if ('IntersectionObserver' in window && cardData.length) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const data = cardData.find((d) => d.card === entry.target);
            if (!data) return;

            if (entry.isIntersecting) {
              whenIdle(() => warm(visibleOf(data.pair)));
            } else {
              data.pair.forEach((v) => idle(v));
            }
          });
        },
        { root: null, threshold: 0 }
      );

      cardData.forEach((d) => io.observe(d.card));
    }
  }

  // Leaving the tab stops any playing video. Nothing resumes on return — videos
  // stay idle on their poster until tapped again. Registered once per page.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    allVideos.forEach((v) => {
      if (v.isConnected) idle(v);
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.testimonial-wrapper-7341').forEach(init);
  });

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
